import type { ApiBody, ApiData } from '@app/api/typed';
import { t } from '@app/i18n';
import type { Identity } from './Identities.api';

export type IdentityAssign = ApiBody<'/api/v1/identities/{id}', 'put'>;
export type IdentityImpact = ApiData<'/api/v1/identities/{id}/impact'>;
export type InfraConfig = ApiData<'/api/v1/infra/config'>;
export type TenantConfig = ApiData<'/api/v1/tenant'>;

/**
 * O que a aba **Identidade** de T5 edita (FR-045). `papel` é `null` enquanto
 * ninguém escolheu: o contrato aceita identidade sem papel, e papel não confere
 * privilégio.
 */
export interface IdentityFormValues {
  slug: string;
  namespace: string;
  /** `<tipo>.<nome>` do controller de topo do pod (aim-xsk.7), ex. `deployment.agt-cobranca`. */
  workload: string;
  /** Informativa (02/10); obrigatória só quando o template do site a usa. */
  service_account: string;
  imagem: string;
  papel: string | null;
  especialidade: string;
  descricao: string;
  /** `Nome <e-mail>` — obrigatório para salvar a atribuição (28/08). */
  dono: string;
  /** Fallback humano do ambiente (D-6/P-03): '' = não declarado. */
  ambiente_declarado: string;
}

/**
 * `donoPadrao` é o `Nome <e-mail>` de quem está logado. A identidade descoberta
 * chega **sem dono**, e quem abre o drawer para atribuir costuma ser quem assume
 * o agente — o campo já vem preenchido com ele. Vale só como valor inicial: o
 * que o servidor gravou nunca é sobrescrito por este padrão.
 */
export function identityDefaults(identidade?: Identity, ownerDefault = ''): IdentityFormValues {
  const sugerido = identidade?.suggestion ?? null;
  return {
    // A sugestão (item 18) só entra onde a identidade **não** tem valor: o que já
    // está gravado é decisão de alguém, e sobrescrevê-lo faria a tela desfazer
    // edição humana toda vez que a imagem fosse republicada.
    slug: identidade?.slug ?? sugerido?.slug ?? '',
    namespace: identidade?.namespace ?? sugerido?.namespace ?? '',
    workload: identidade?.workload ?? sugerido?.workload ?? '',
    service_account: identidade?.service_account ?? '',
    // A imagem vem pré-preenchida do discovery (FR-045).
    imagem: identidade?.image ?? '',
    papel: identidade?.role ?? sugerido?.role ?? null,
    especialidade: identidade?.specialty ?? '',
    descricao: identidade?.description ?? '',
    // O dono sugerido vem **antes** do «(você)»: quem a imagem declara é quem
    // responde pelo agente; quem abriu o drawer só assume quando ninguém foi
    // declarado.
    dono: identidade?.owner ?? sugerido?.owner ?? ownerDefault,
    ambiente_declarado: identidade?.declared_environment ?? '',
  };
}

/** De onde a plataforma tirou um campo pré-preenchido — o chip de T5 (item 18). */
export type FieldOrigin = 'imagem' | 'cluster';

/**
 * O texto do chip: «da imagem» / «do cluster».
 *
 * Lê do catálogo a cada chamada. Como tabela de módulo, o texto ficaria
 * congelado no idioma que estava ativo quando o arquivo foi importado.
 */
export function originLabel(origem: FieldOrigin): string {
  return t(`identities.form.origin.${origem}`);
}

/**
 * A origem a mostrar ao lado de um campo, ou `null` quando não há chip.
 *
 * Só há chip enquanto o campo **está de fato vindo da sugestão** — ou seja,
 * enquanto a identidade não tem o valor gravado. Depois de salvo, o valor é de
 * quem salvou (mesmo que a pessoa tenha aceitado a sugestão sem tocar nela), e
 * continuar marcando «da imagem» diria que a plataforma decidiu o que uma pessoa
 * decidiu.
 */
export function fieldOrigin(
  identidade: Identity | undefined,
  campo: string,
  stored: string | null | undefined,
): FieldOrigin | null {
  if (stored != null && stored !== '') return null;
  const origem = identidade?.suggestion?.origin_by_field?.[campo];
  return origem === 'imagem' || origem === 'cluster' ? origem : null;
}

/**
 * Corpo de `assignIdentity`. `version` é a lida na tela: divergente → 409
 * `versao_antiga` (FR-124), e é assim que duas pessoas editando a mesma
 * identidade não se sobrescrevem em silêncio.
 */
export function forAssignment(valores: IdentityFormValues, version: number): IdentityAssign {
  const texto = (valor: string) => valor.trim();
  return {
    slug: texto(valores.slug),
    namespace: texto(valores.namespace),
    workload: texto(valores.workload),
    // Vai sempre: vazio apaga, e o formulário é dono do campo. Ausente (outro cliente)
    // mantém o gravado — é o servidor que distingue.
    service_account: texto(valores.service_account),
    image: texto(valores.imagem),
    role: valores.papel,
    specialty: texto(valores.especialidade) || null,
    description: texto(valores.descricao) || null,
    // `dono` é obrigatório na atribuição: vai como string, nunca como `null` —
    // vazio o servidor recusa com 422 `dados_invalidos` no campo `dono`, e o yup
    // já barra antes.
    owner: texto(valores.dono),
    declared_environment: (texto(valores.ambiente_declarado) || null) as IdentityAssign['declared_environment'],
    version,
  };
}

/**
 * `{registry}/{owner}` do grupo **registry** de `getInfraConfig` — o prefixo que
 * FR-046 exige na imagem. `null` quando a configuração não está legível: aí a
 * validação local sai de cena e quem recusa é o servidor (422
 * `imagem_fora_do_registry`).
 */
export function registryExpected(config?: InfraConfig): string | null {
  const grupo = config?.groups?.find((g) => g.name === 'registry');
  const valor = (campo: string) => {
    const found = grupo?.fields?.find((c) => c.field === campo)?.value;
    return typeof found === 'string' ? found.trim() : '';
  };
  const registry = valor('release_registry');
  const owner = valor('release_owner');
  if (!registry) return null;
  // Owner vazio é o ACR do lab (P-02): a imagem precisa estar no host, só nele.
  return owner ? `${registry}/${owner}` : registry;
}

/** A mensagem literal do FR-046. */
export function registryError(prefix: string): string {
  return t('identities.form.validation.registry', { prefix });
}

/** Explicação dos campos congelados (FR-048). */
export function textFrozen(contractVersion: number | null | undefined): string {
  return t('identities.form.frozenHelp', { version: contractVersion ?? 1 });
}
